import {useEffect, useCallback} from 'react';

import type {UseManagedShippingProps as Props, ArtaShippingEstimate, ArtaLocation} from './typings';

import useLocalStorage from '@/hooks/use-local-storage/use-local-storage';
import constantsFactory from '@/utils/constants';
import {on, off} from '@/utils/events';

import useShippingEstimate from './use-shipping-estimate';
import {unixTimestamp, isCachedValid, getArtaQuoteTypes} from './utils';

const {EVENTS} = constantsFactory();
const STORAGE_KEY = 'artaShippingEstimate';

/**
 * @description Custom hook to manage the Arta managed shipping estimates.
 * - uses the `useShippingEstimate` hook to manage ARTA API fetching and state
 * - uses the `useLocalStorage` hook to cache the last successful estimate
 * - invalidates the cache based on expiry and changes in origin, product or currency or user log out
 * - provides a function to conditionally fetch new estimates
 * @param {object} params - The parameters for the hook.
 * @param {object} params.origin - The origin location.
 * @param {object[]} params.objects - The list of product objects to ship.
 * @param {'CAD' | 'EUR' | 'GBP' | 'HKD' | 'USD'} params.currency - The currency for the shipping estimate.
 * @param {string} params.productSlug - The product slug, used for identifying the estimate.
 * @param {boolean|undefined} params.isEnabled - Flag to enable or disable the managed shipping functionality.
 * @param {string[] | undefined } params.quoteTypes - Optional array of quote types to quote estimates.
 * @returns {object} An object containing:
 * - the final estimate state,
 * - the function to fetch new estimates.
 */
const useManagedShipping = ({
  origin,
  objects,
  currency,
  productSlug,
  isEnabled,
  quoteTypes,
}: Props) => {
  const {estimate, session, handleGetEstimate, setEstimate} = useShippingEstimate({
    origin,
    objects,
    currency,
    productSlug,
    isEnabled,
    quoteTypes: getArtaQuoteTypes(quoteTypes),
  });

  const [storedEstimate, setStoredEstimate] = useLocalStorage<ArtaShippingEstimate | null>(
    STORAGE_KEY,
    null,
  );

  /** check if cached value is still valid and relevant */
  const isStoredBaseValid =
    storedEstimate &&
    !unixTimestamp.isExpired(storedEstimate.timestamp) &&
    (storedEstimate.from.city ?? '') === (origin.city ?? '') &&
    storedEstimate.from.country === origin.country &&
    storedEstimate.currency === currency &&
    storedEstimate.product_slug === productSlug;

  /**
   * Final estimate
   * - prefer fresh state, then cached if still valid and relevant
   */
  let finalEstimate = estimate.data || (isStoredBaseValid ? storedEstimate : null);
  let finalStatus = estimate.data
    ? estimate.status
    : isStoredBaseValid
      ? 'success'
      : estimate.status;

  if (estimate.status === 'error') {
    /** however, error case should propagate into final estimate return value */
    finalEstimate = null;
    finalStatus = 'error';
  }

  /**
   * @description Function to fetch a new estimate for a given destination.
   * - if managed shipping is disabled, sets error state
   * - if cached estimate is still valid and relevant, uses it instead of fetching
   * - otherwise, calls the `handleGetEstimate` function from `useShippingEstimate` to fetch a new estimate
   * @param {object} destination - The destination location for the shipping estimate.
   * @returns {void|Promise<void>} - a void or void promise procedure function that updates state when the estimate is fetched.
   */
  const fetchEstimate = useCallback(
    (destination: ArtaLocation) => {
      if (!isEnabled) {
        return setEstimate({status: 'error', data: null, error: 'Managed shipping not enabled'});
      }
      if (
        isStoredBaseValid &&
        storedEstimate &&
        isCachedValid(
          {
            to: storedEstimate.to,
            from: storedEstimate.from,
            product_slug: storedEstimate.product_slug,
            currency: storedEstimate.currency,
          },
          {to: destination, from: origin, product_slug: productSlug, currency},
        )
      ) {
        setEstimate({status: 'success', data: storedEstimate, error: null});
        return;
      }

      /** Otherwise, fetch new estimate */
      return handleGetEstimate(destination);
    },
    [isStoredBaseValid, storedEstimate, origin, productSlug, session.data?.id, currency],
  );

  /**
   * effect, on fetch success → persist
   */
  useEffect(() => {
    if (estimate.status === 'success' && estimate.data) {
      setStoredEstimate((current) =>
        current && current.timestamp === estimate.data!.timestamp ? current : estimate.data,
      );
    }
  }, [estimate.status, estimate.data, setStoredEstimate]);

  /** Clear expired cache */
  useEffect(() => {
    if (storedEstimate && unixTimestamp.isExpired(storedEstimate.timestamp)) {
      setStoredEstimate(null);
    }
  }, [storedEstimate, setStoredEstimate]);

  /** Clear cache on user logout */
  useEffect(() => {
    const handleClear = () => {
      setEstimate({status: 'idle', data: null, error: null});
      setStoredEstimate(null);
    };
    on(EVENTS.LOGOUT_SUCCESS, handleClear);
    return () => off(EVENTS.LOGOUT_SUCCESS, handleClear);
  }, [setStoredEstimate]);

  /**
   * Reset estimate when
   * - product changes
   * - currency changes
   * (we keep the cache, in case user comes back to the same product)
   */
  useEffect(() => {
    setEstimate({data: null, status: 'idle', error: null});
  }, [productSlug, currency]);

  return {
    estimate: finalEstimate,
    status: finalStatus,
    error: estimate.error,
    fetchEstimate,
  };
};

export default useManagedShipping;
